import React, { useRef, useState } from "react"
import { toAbsoluteUrl } from "@common-metronic/helpers"
import MyLoadingOverlay from "../../../../../common/components/MyLoadingOverlay"
import { IGetAssurancesResponseItemModel } from "./core/_models"
import { useAssurancesListingContext } from "./AssurancesListing"
import { useAssurancesListingListContext } from "./AssurancesListingList"
import { getSelectedItemPlusPreviousAndNextItem } from "./core/_helpers"
import { useAuth } from "../../../../AppContext"
import { useThemeMode } from "@common-metronic/partials"
import MyTooltip from "@common-utils/MyTooltip"
import { Link } from "react-router-dom"
import FileAndDriveHelper from "@common-helpers/FileAndDriveHelper"

const ListItem = ({ item, handleClickItemForPreview, isItemSelected, isItemLoading }: IListItemPropsModel) => {
     const { currentUser } = useAuth()
     const { mode } = useThemeMode()
     const globalContext = useAssurancesListingContext()
     const listContext = useAssurancesListingListContext()

     // Separate loading states for main avatar and manager avatar
     const [isAvatarLoading, setIsAvatarLoading] = useState<boolean>(true)
     const [isRespAvatarLoading, setIsRespAvatarLoading] = useState<boolean>(true)

     const checkboxInputRef = useRef<any>(null)

     const isItemBeingUpdated = !!globalContext.itemsBeingUpdated.find(item_ => item_.mission.id === item.mission.id)

     // Alias for the manager (Responsable) to keep code clean
     const responsable = item.mission.consultant?.personnel

     function handleOnCheckItem() {
          const isItemChecked = globalContext.isBulkSelectionEnabled.checkedElementsInPage.find((item_: IGetAssurancesResponseItemModel) => item_.mission.id === item.mission.id)
          if (!isItemChecked) {
               globalContext.setIsBulkSelectionEnabled(prev => ({
                    ...prev,
                    checkedElementsInPage: [...prev.checkedElementsInPage, item],
                    areAllElementsInAllPagesChecked: false,
               }))
          } else {
               globalContext.setIsBulkSelectionEnabled(prev => ({
                    ...prev,
                    checkedElementsInPage: prev.checkedElementsInPage.filter((item_: IGetAssurancesResponseItemModel) => item_.mission.id !== item.mission.id),
                    areAllElementsInAllPagesChecked: false,
               }))
          }
     }

     // Determine status badge style
     const statusBadgeClass = item.mission.assuranceCheckedBy ? "badge-light-success text-success" : "badge-light-danger text-danger"
     const statusIconClass = item.mission.assuranceCheckedBy ? "fa-check-circle text-success" : "fa-exclamation-circle text-danger"
     const statusLabel = item.mission.assuranceCheckedBy ? "Vérifié" : "Non vérifié"

     return (
          <MyLoadingOverlay isActive={isItemLoading || isItemBeingUpdated}>
               <div
                    ref={isItemSelected ? listContext.selectedItemRef : null}
                    onClick={e => handleClickItemForPreview(e)}
                    className={`card mb-3 transition-all duration-300 ${
                         isItemSelected ? "bg-light-primary border border-dashed border-primary shadow-sm" : "bg-body hover-elevate-up border border-gray-200 cursor-pointer"
                    }`}
               >
                    <div className="card-body p-4">
                         <div className="d-flex align-items-center">
                              {/* SECTION 1: Checkbox & Avatar */}
                              <div className="d-flex align-items-center me-4">
                                   {globalContext.isBulkSelectionEnabled.enabled && (
                                        <div className="form-check form-check-custom form-check-solid form-check-sm me-4 not-concerned-by-preview">
                                             <input
                                                  type="checkbox"
                                                  className="form-check-input cursor-pointer border border-gray-300 not-concerned-by-preview"
                                                  style={{ height: "1.5rem", width: "1.5rem" }}
                                                  checked={
                                                       !!globalContext.isBulkSelectionEnabled.checkedElementsInPage.find(
                                                            (item_: IGetAssurancesResponseItemModel) => item.mission.id === item_.mission.id
                                                       )
                                                  }
                                                  onChange={() => handleOnCheckItem()}
                                                  ref={checkboxInputRef}
                                             />
                                        </div>
                                   )}

                                   <MyLoadingOverlay isActive={isAvatarLoading} styles={{ height: "45px", width: "45px", borderRadius: "10px" }} size={"sm"}>
                                        <div className="symbol symbol-45px symbol-square">
                                             <img
                                                  src={toAbsoluteUrl(mode == "dark" ? "/media/svg/files/blank-image-dark.svg" : "/media/svg/files/blank-image.svg")}
                                                  onLoad={() => setIsAvatarLoading(false)}
                                                  className="rounded-2"
                                                  alt="Avatar"
                                             />
                                        </div>
                                   </MyLoadingOverlay>
                              </div>

                              {/* SECTION 2: Main Info (Consultant & Mission) */}
                              <div className="d-flex flex-column flex-grow-1 min-w-150px min-w-0">
                                   <div className="d-flex align-items-center mb-1">
                                        <Link
                                             to={`/business/consultants/${item.mission.consultant?.id}/details`}
                                             target={"_blank"}
                                             className="text-gray-900 text-hover-primary fs-6 fw-bolder me-2 text-truncate"
                                             style={{ maxWidth: "250px" }}
                                             onClick={e => e.stopPropagation()}
                                             title={item.mission.consultant?.prenomNom}
                                        >
                                             {item.mission.consultant?.prenomNom}
                                        </Link>

                                        <span
                                             className="badge badge-light fs-9 fw-bold px-2 py-1 text-truncate d-inline-block"
                                             style={{ maxWidth: "150px", verticalAlign: "middle" }}
                                             title={item.mission.consultant?.metier?.nom}
                                        >
                                             {item.mission.consultant?.metier?.nom || "N/A"}
                                        </span>
                                   </div>
                                   <div className="d-flex align-items-center text-muted fs-7 fw-bold">
                                        <i className="fas fa-file-contract fs-8 me-1 text-gray-400"></i>
                                        <Link
                                             to={`/client/ptf/${item.mission.propal?.id}/details`}
                                             target={"_blank"}
                                             className="text-gray-600 text-hover-primary me-3 text-truncate"
                                             onClick={e => e.stopPropagation()}
                                        >
                                             {item.mission.reference}
                                        </Link>

                                        {item.mission.assuranceBu && (
                                             <>
                                                  <span className="bullet bullet-dot bg-gray-300 mx-2 flex-shrink-0"></span>
                                                  <span className="text-gray-500 text-truncate">{item.mission.assuranceBu.nom}</span>
                                             </>
                                        )}
                                   </div>
                              </div>

                              {/* SECTION 3: Dates */}
                              <div className="d-none d-xl-flex flex-column align-items-end justify-content-center me-6 min-w-100px">
                                   <div className="text-gray-800 fs-7 fw-bolder">{item.mission.dateDebutValidite?.format("DD MMM YYYY")}</div>
                                   <div className="text-gray-500 fs-8 fw-bold">au {item.mission.dateFinValidite?.format("DD MMM YYYY")}</div>
                              </div>

                              {/* SECTION 4: Status Badge */}
                              <div className="d-flex align-items-center justify-content-end min-w-100px">
                                   {item.mission.assuranceCheckedBy ? (
                                        <MyTooltip title={`Vérifié le ${item.mission.assuranceCheckedDate?.format("DD/MM/YYYY")} par ${item.mission.assuranceCheckedBy.prenomNom}`}>
                                             <div className={`badge ${statusBadgeClass} d-flex align-items-center px-3 py-2`}>
                                                  <i className={`fas ${statusIconClass} fs-8 me-2`}></i>
                                                  {statusLabel}
                                             </div>
                                        </MyTooltip>
                                   ) : (
                                        <div className={`badge ${statusBadgeClass} d-flex align-items-center px-3 py-2`}>
                                             <i className={`fas ${statusIconClass} fs-8 me-2`}></i>
                                             {statusLabel}
                                        </div>
                                   )}
                              </div>
                         </div>

                         {/* EXPANDED DETAILS (When Selected) */}
                         {isItemSelected && (
                              <div className="mt-4 pt-4 border-top border-gray-200 border-dashed animate__animated animate__fadeIn">
                                   <div className="row g-5 align-items-center">
                                        {/* Manager Info with Photo Avatar */}
                                        <div className="col-md-6 d-flex align-items-center">
                                             <div className="me-3">
                                                  <MyLoadingOverlay isActive={isRespAvatarLoading} styles={{ height: "40px", width: "40px", borderRadius: "50%" }} size={"sm"}>
                                                       <img
                                                            src={
                                                                 responsable?.avatarGoogleDriveId
                                                                      ? FileAndDriveHelper.getThumbnailDriveUrl(responsable?.avatarGoogleDriveId)
                                                                      : toAbsoluteUrl(`/media/svg/avatars/${mode === "light" ? "blank" : "blank-dark"}.svg`)
                                                            }
                                                            onLoad={() => setIsRespAvatarLoading(false)}
                                                            onError={() => setIsRespAvatarLoading(false)}
                                                            style={{ height: "40px", width: "40px", borderRadius: "50%", objectFit: "cover" }}
                                                            alt={responsable?.prenomNom || "Avatar"}
                                                       />
                                                  </MyLoadingOverlay>
                                             </div>
                                             <div className="d-flex flex-column">
                                                  <span className="text-gray-500 fs-8 fw-bold text-uppercase">Responsable</span>
                                                  <span className="text-gray-800 fw-bolder fs-7 text-truncate" style={{ maxWidth: "200px" }}>
                                                       {responsable?.prenomNom}
                                                  </span>
                                             </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="col-md-6 d-flex align-items-center justify-content-end">
                                             {!item.mission.assuranceCheckedBy ? (
                                                  // Button: Mark as Verified
                                                  <MyTooltip title={"Valider la conformité de l'assurance"}>
                                                       <button
                                                            type={"button"}
                                                            className="btn btn-sm btn-light-success btn-active-success fw-bold d-flex align-items-center border border-success"
                                                            onClick={e => {
                                                                 e.stopPropagation()
                                                                 globalContext.handleCheckAssurancesAsVerified([item])
                                                            }}
                                                       >
                                                            <i className="fas fa-check-circle fs-4 me-2" />
                                                            Marquer comme conforme
                                                       </button>
                                                  </MyTooltip>
                                             ) : (
                                                  // Button: Mark as Not Verified (Opposite action)
                                                  <MyTooltip title={"Annuler la conformité de l'assurance"}>
                                                       <button
                                                            type={"button"}
                                                            className="btn btn-sm btn-light-danger btn-active-danger fw-bold d-flex align-items-center border border-danger"
                                                            onClick={e => {
                                                                 e.stopPropagation()
                                                                 globalContext.handleCheckAssurancesAsNonVerified([item])
                                                            }}
                                                       >
                                                            <i className="fas fa-times-circle fs-4 me-2" />
                                                            Non conforme
                                                       </button>
                                                  </MyTooltip>
                                             )}
                                        </div>
                                   </div>
                              </div>
                         )}
                    </div>
               </div>
          </MyLoadingOverlay>
     )
}

const AssurancesListingListItem = ({ item, index }: { item: IGetAssurancesResponseItemModel; index: number }) => {
     const context = useAssurancesListingContext()

     const isItemSelected: boolean = context.itemInPreview !== null && context.itemInPreview.selectedItem.mission.id === item.mission.id
     const isItemLoading: boolean =
          context.itemInPreview !== null && context.itemInPreview.selectedItem.mission.id === item.mission.id && context.itemInPreview.isSelectedItemLoading

     function handleClickItemForPreview(e: React.MouseEvent<any>) {
          const clickedElement = e.target as HTMLElement
          if (
               !clickedElement.closest(".not-concerned-by-preview") &&
               !clickedElement.classList.contains("not-concerned-by-preview") &&
               context.itemInPreview?.selectedItem.mission.id !== item.mission.id
          ) {
               context.setAreFiltersVisible(false)

               context.setItemInPreview(
                    getSelectedItemPlusPreviousAndNextItem(item, index, context.listingQuery.data!.data, context.listingQuery.data!.page, context.listingQuery.data!.totalPages)
               )
          }
     }

     return <ListItem item={item} handleClickItemForPreview={handleClickItemForPreview} isItemSelected={isItemSelected} isItemLoading={isItemLoading} />
}

interface IListItemPropsModel {
     item: IGetAssurancesResponseItemModel
     handleClickItemForPreview: (e: React.MouseEvent<any>) => void
     isItemSelected: boolean
     isItemLoading: boolean
}

export default AssurancesListingListItem
